import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { OurUseQueryOptions } from "@nens/react-query-utils";
import { PaginatedTemplateList, getHCCApi } from "@nens/threedi/openapi-client";

import { invalidateIfOutdated } from "./hooks/if-outdated";

const getHccSimulationTemplatesQuery = (
  threediModelId: number | undefined
): OurUseQueryOptions<PaginatedTemplateList> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-simulation-templates", threediModelId],
    queryFn:
      threediModelId !== undefined
        ? () =>
            getHCCApi().simulationTemplatesList({
              simulationThreedimodelId: threediModelId,
            })
        : skipToken,
  });
};

export const useHccSimulationTemplatesQuery = (threediModelId: number | undefined) =>
  useQuery(getHccSimulationTemplatesQuery(threediModelId));

// INVALIDATION

export const useUpdateHccSimulationTemplatesQuery = (threediModelId: number | undefined) => {
  const queryClient = useQueryClient();
  return ({ id, name }: { id: number; name: string }) => {
    queryClient.setQueryData(
      getHccSimulationTemplatesQuery(threediModelId).queryKey,
      // Take the previous data which is a list. Look through that list and only update the name of the template with the given id. Return the updated list to the update function.
      (previous: PaginatedTemplateList | undefined) => {
        if (!previous) return previous;
        return {
          ...previous,
          results: previous.results.map((template) =>
            template.id === id ? { ...template, name } : template
          ),
        };
      }
    );
  };
};

export const useInvalidateHccSimulationTemplatesQuery = (threediModelId: number | undefined) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: getHccSimulationTemplatesQuery(threediModelId).queryKey,
    });
  };
};

export const useInvalidateIfOutdatedHccSimulationTemplatesQuery = (
  threediModelId: number | undefined
) => {
  const queryClient = useQueryClient();
  return (finishedAt: Date) => {
    invalidateIfOutdated(
      queryClient,
      finishedAt,
      getHccSimulationTemplatesQuery(threediModelId).queryKey
    );
  };
};
